/**
 * Diagnostics for an ATTACHED screen context of the HaulierMagic Assistant (CU-14zefhy1qnh): console errors, failed and
 * slow requests, permissions / disabled actions, fuller validation, errors on screen, environment and the tenant
 * settings a screen read. Collected cheaply all the time, sent only inside a context the user attached with 🧭.
 * Everything is redacted: no query strings, no tokens, no e-mail addresses, no secret setting values.
 *
 * No Angular imports here so the helpers can be unit-tested without a TestBed.
 */
import {
    AssistantConsoleError,
    AssistantFailedRequest,
    AssistantScreenContext,
    byteLength,
    capContext,
    clip,
    FormLike,
    isSensitiveName,
    pathOnly,
    VALUE_MAX
} from 'app/blocks/service/assistant-context/assistant-context';
import {isAssistantApiUrl} from 'app/blocks/util/assistant-url';

export const DIAG_MAX = 10;
export const RECENT_MS = 15 * 60 * 1000;
export const SLOW_MS = 5000;
export const SLOW_MAX = 5;
export const CHECKS_MAX = 30;
export const DISABLED_ACTIONS_MAX = 15;
export const SETTINGS_MAX = 30;
export const VISIBLE_ERRORS_MAX = 5;
export const REQUIRED_EMPTY_MAX = 20;
export const API_FIELD_ERRORS_MAX = 15;
/** one attached context, label and diagnostics included */
export const ATTACHED_CONTEXT_MAX_BYTES = 6144;

const SECRET_KEY = /secret|token|password|key|pwd|pass(?!enger)|credential|auth(?!or)/i;
const EMAIL = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
/** an e-mail in a percent-encoded URL segment ("a.b%40x.com") */
const EMAIL_ENCODED = /[A-Za-z0-9._+-]+%40[A-Za-z0-9.-]+\.[A-Za-z]{2,}/gi;
const BEARER = /\b(bearer|basic)\s+[A-Za-z0-9._~+/=-]+/gi;
const JWT = /\beyJ[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}/g;
const URL_QUERY = /((?:https?:\/\/|\/)[^\s?#"'<>]*)\?[^\s#"'<>]*/g;
const SECRET_PAIR =
    /\b([A-Za-z0-9_-]{0,40}?(?:token|api[_-]?key|apikey|password|passwd|passcode|pwd|secret|authorization|credential)[A-Za-z0-9_-]{0,40})["']?\s*[=:]\s*("[^"]*"|'[^']*'|[^\s,;&}\]]+)/gi;
/** RMA-15: redact() reads at most this many times `max` characters (the regexes are quadratic on long input). */
const REDACT_INPUT_FACTOR = 4;

/** Strips query strings, tokens, secrets in `key=value` text and e-mail addresses; collapses whitespace; cuts to `max`. */
export function redact(text: any, max = 300): string {
    if (text === null || text === undefined) {
        return '';
    }
    const s = String(text)
        .substring(0, Math.max(max, 1) * REDACT_INPUT_FACTOR)
        .replace(JWT, '[token]')
        .replace(BEARER, '$1 [token]')
        .replace(SECRET_PAIR, '$1=[hidden]')
        .replace(URL_QUERY, '$1')
        .replace(EMAIL, '[email]')
        .replace(EMAIL_ENCODED, '[email]');
    return clip(s, max);
}

/**
 * X1 (CU-14zefhy29r7): a URL as sent to the model: path only (no query, no origin), with e-mails and tokens in its
 * segments masked (e.g. `/setup/organisations/<e-mail>` → `/setup/organisations/[email]`).
 */
export function redactPath(url: string, max = 200): string {
    return redact(pathOnly(url), max) || '/';
}

const OBSERVED_ASSET = /\.(png|jpe?g|gif|svg|webp|ico|woff2?|ttf|otf|eot)$/i;

/**
 * Whether a failed load seen by the resource-timing observer goes into `failedRequests`. Same rules as the interceptor
 * (`recordApiError`): no 401s (a session ending is not an error to explain, X3 CU-14zefhy29r7), no CoPilot polling;
 * images, fonts, stylesheets and map tiles only from 500 up.
 */
export function recordsObservedFailure(name: string, status: number, initiatorType?: string): boolean {
    if (!status || status < 400 || status === 401) {
        return false;
    }
    const path = pathOnly(name);
    const asset = ['img', 'image', 'css', 'link'].indexOf(initiatorType) >= 0 || OBSERVED_ASSET.test(path) || /tile/i.test(path);
    return !(asset && status < 500) && recordsFailedCall(path, status);
}

/** A stable key for the signed-in user (tenant + user id / login), '' when nobody is signed in. */
export function contextOwnerKey(identity: any): string {
    if (!identity || typeof identity !== 'object') {
        return '';
    }
    const user = identity.id ?? identity.login ?? identity.email ?? '';
    return user === '' || user === null ? '' : `${identity.tenantId ?? ''}|${user}`;
}

/**
 * X2/X3 (CU-14zefhy29r7): the context belongs to one signed-in user. `owner` is whom it was collected for (undefined =
 * nobody seen yet: app start, kept so the first user's start-up errors stay; '' = signed out). A sign-in of a user other
 * than the owner (another user, or anyone after a sign-out or an expired session) starts a fresh context, so nothing of
 * the previous session (incl. what its page logged after the logout) reaches the next user.
 */
export function ownerChange(owner: string | undefined, identity: any): {owner: string | undefined; reset: boolean} {
    const key = contextOwnerKey(identity);
    if (!key) {
        // signed out: remember it, so the next sign-in starts fresh; nothing seen yet stays "nobody yet"
        return {owner: owner === undefined ? undefined : '', reset: false};
    }
    return {owner: key, reset: owner !== undefined && owner !== key};
}

/** What `watchSignedInUser` needs of Principal: its authentication state stream. */
export interface AuthStateSource {
    getAuthenticationState(): {subscribe(next: (identity: any) => void): unknown};
}

/**
 * X2/X3 (CU-14zefhy29r7): calls `reset` whenever another user signs in (`ownerChange`). Reads the principal a microtask
 * later (Principal -> HttpClient -> interceptors -> AssistantContextService is a cycle at construction); never throws.
 */
export function watchSignedInUser(principal: () => AuthStateSource | null | undefined, reset: () => void): Promise<void> {
    return Promise.resolve().then(() => {
        try {
            let owner: string | undefined;
            principal()
                ?.getAuthenticationState()
                .subscribe((identity) => {
                    const next = ownerChange(owner, identity);
                    if (next.reset) {
                        reset();
                    }
                    owner = next.owner;
                });
        } catch (e) {
            // no Principal (tests): logout() still resets
        }
    });
}

/** RMA-32: a trail entry (label, detail) with both texts redacted; returns a new object. */
export function redactTrailEntry<T extends {label: string; detail?: string}>(entry: T): T {
    const out: T = {...entry, label: redact(entry.label, VALUE_MAX)};
    if (entry.detail) {
        out.detail = redact(entry.detail, VALUE_MAX);
    }
    return out;
}

/** RMA-51: whether a succeeded call makes the field errors of an earlier 400 stale (a page write; not the CoPilot's own calls). */
export function clearsFieldErrors(method: string, url: string): boolean {
    return !!url && /^(POST|PUT|PATCH)$/i.test(method || '') && !isAssistantApiUrl(url);
}

/** RMA-51: a successful write clears the stored field errors only when it hits the same path (query ignored) as the failed call. */
export function clearsFieldErrorsOf(failedApiPath: string | undefined, method: string, url: string): boolean {
    return clearsFieldErrors(method, url) && !!failedApiPath && pathOnly(url) === failedApiPath;
}

/** Appends (newest last) and keeps the last `max`. */
export function pushRing<T>(list: T[], entry: T, max = DIAG_MAX): T[] {
    const next = (list || []).concat(entry);
    return next.length > max ? next.slice(next.length - max) : next;
}

/** Entries from the last `windowMs` (by their ISO `at`). */
export function recent<T extends {at: string}>(list: T[], nowMs: number, windowMs = RECENT_MS): T[] {
    return (list || []).filter((e) => {
        const t = Date.parse(e && e.at);
        return !isNaN(t) && nowMs - t <= windowMs;
    });
}

const CONSOLE_NOISE = [
    /ResizeObserver loop/i,
    // only the wording HttpClient / fetch / XHR use for an aborted request: "cancelled" is an everyday word in
    // HaulierMagic (orders, runs), so "Cannot edit a cancelled run" must still reach the attached context
    /\bAbortError\b/,
    /The user aborted a request/i,
    /The operation was aborted/i,
    /signal is aborted/i,
    /net::ERR_ABORTED/i,
    /\b(request|fetch|xhr|http)\b[^\n]{0,40}\b(aborted|cancel(l)?ed)\b/i,
    /Assistant (screen|screenshot|file|plan)/i,
    /ExpressionChangedAfterItHasBeenChecked/i
];

/** Console messages that are not worth the assistant's time (ResizeObserver loops, aborted requests, our own logs). */
export function isConsoleNoise(message: string): boolean {
    return !message || CONSOLE_NOISE.some((re) => re.test(message));
}

/** "https://x/app/main.js?v=3" + 12 + 5 → "main.js:12:5". */
export function sourceOf(file: string, line?: number, column?: number): string {
    if (!file) {
        return '';
    }
    const name = pathOnly(file).split('/').pop() || '';
    if (!name) {
        return '';
    }
    return clip(name + (line ? ':' + line + (column ? ':' + column : '') : ''), 120);
}

/** First "file:line:col" of an Error stack, if any. */
export function sourceFromStack(stack: string): string {
    const m = /\(?((?:https?:\/\/|\/)[^\s()]+?):(\d+):(\d+)\)?/.exec(stack || '');
    return m ? sourceOf(m[1], +m[2], +m[3]) : '';
}

/** Readable text of `console.error` arguments (Errors by message, objects as short JSON). */
export function consoleText(args: any[]): string {
    return (args || [])
        .map((a) => {
            if (a instanceof Error || (a && typeof a === 'object' && typeof a.message === 'string' && typeof a.name === 'string')) {
                return a.message;
            }
            if (a && typeof a === 'object') {
                if (typeof a.status === 'number' && (a.url || a.message)) {
                    return `HTTP ${a.status} ${a.message || ''}`; // HttpErrorResponse
                }
                try {
                    return JSON.stringify(a).substring(0, 300);
                } catch (e) {
                    return '[object]';
                }
            }
            return String(a);
        })
        .join(' ');
}

/** A console error entry, or null when it is noise. */
export function consoleErrorEntry(message: string, source: string, at: string): AssistantConsoleError | null {
    const text = redact(message, 300);
    if (isConsoleNoise(text)) {
        return null;
    }
    const entry: AssistantConsoleError = {at, message: text};
    if (source) {
        entry.source = source;
    }
    return entry;
}

/** The server's short message of a failed call (problem+json detail/title, field errors, short text) — never bodies. */
export function errorMessage(error: any): string {
    if (error && typeof error === 'object' && !(typeof Blob !== 'undefined' && error instanceof Blob)) {
        const fieldErrors = apiFieldErrors(error);
        if (fieldErrors.length) {
            return fieldErrors
                .slice(0, 5)
                .map((fe) => `${fe.field}: ${fe.message}`)
                .join('; ');
        }
        const m = error.detail || error.title || error.message || '';
        return typeof m === 'string' ? m : '';
    }
    if (typeof error === 'string') {
        const text = error.trim();
        return text && !text.startsWith('<') && !text.startsWith('{') && text.length <= 200 ? text : '';
    }
    return '';
}

/**
 * Whether a failed call goes into `failedRequests` / the trail (CU-14zefhy1r4j U2). The CoPilot's own failures count too
 * (a history `transcript` 404 must be nameable), except the `chat/events/` polling, which is noise. Failures only: timings
 * of assistant calls stay out (`isAssistantApiUrl`). Only the path counts, never the query.
 */
export function recordsFailedCall(url: string, status?: number): boolean {
    if (!url) {
        return false;
    }
    const path = pathOnly(url);
    if (/(^|\/)api\/assistant\/chat\/events(\/|$)/.test(path)) {
        return false;
    }
    // RMA-50: the CoPilot's own 404s are expected answers ("plan expired", "request already closed", "task gone"), not
    // failures to explain; only a missing history `transcript` stays nameable
    if (status === 404 && /(^|\/)api\/assistant(\/|$)/.test(path) && !/\/transcript(\/|$)/.test(path)) {
        return false;
    }
    return true;
}

export function failedRequestEntry(
    method: string,
    url: string,
    status: number,
    error: any,
    durationMs: number | null,
    at: string
): AssistantFailedRequest {
    // X1 (CU-14zefhy29r7): the path is masked like the message (an e-mail in a segment never reaches the model)
    const entry: AssistantFailedRequest = {at, method: (method || 'GET').toUpperCase(), path: redactPath(url, 200), status: status || 0};
    const message = redact(errorMessage(error), 200);
    if (message) {
        entry.message = message;
    }
    if (typeof durationMs === 'number' && durationMs >= 0) {
        entry.durationMs = Math.round(durationMs);
    }
    return entry;
}

/** `{field, message}` pairs of a 400 problem+json body (`fieldErrors` / `violations`); sensitive fields left out. */
export function apiFieldErrors(error: any): Array<{field: string; message: string}> {
    const list: any[] = error && typeof error === 'object' ? error.fieldErrors || error.violations : null;
    if (!Array.isArray(list)) {
        return [];
    }
    return list
        .filter((fe) => fe && (fe.field || fe.objectName) && !isSensitiveName(fe.field))
        .slice(0, API_FIELD_ERRORS_MAX)
        .map((fe) => ({field: clip(fe.field || fe.objectName, 80), message: redact(fe.message || fe.defaultMessage || '', 160)}));
}

/** Paths of enabled controls whose only problem may be that they are required and empty (`errors.required`); values are never read. */
export function requiredEmptyPaths(form: FormLike, max = REQUIRED_EMPTY_MAX): string[] {
    const out: string[] = [];
    const walk = (control: FormLike, path: string, depth: number) => {
        if (!control || control.disabled || depth > 6 || out.length >= max) {
            return;
        }
        if (path && isSensitiveName(path.split('.').pop())) {
            return;
        }
        if (path && control.errors && control.errors.required) {
            out.push(path);
        }
        const children = control.controls;
        if (Array.isArray(children)) {
            children.forEach((c, i) => walk(c, path ? `${path}.${i}` : String(i), depth + 1));
        } else if (children) {
            Object.keys(children).forEach((k) => walk(children[k], path ? `${path}.${k}` : k, depth + 1));
        }
    };
    walk(form, '', 0);
    return out;
}

/** True for a setting key whose value must never leave the browser. */
export function isSecretKey(key: string): boolean {
    return SECRET_KEY.test(key || '') || isSensitiveName(key);
}

/** A setting value as text for the assistant ("[hidden]" for secret-looking keys). */
export function settingValue(key: string, value: any): string {
    if (isSecretKey(key)) {
        return '[hidden]';
    }
    if (value === null || value === undefined) {
        return '';
    }
    return redact(typeof value === 'object' ? JSON.stringify(value) : value, 120);
}

/** "Chrome 129" / "Windows 10" from a user agent (good enough for support questions). */
export function browserInfo(ua: string): {browser?: string; os?: string} {
    const out: {browser?: string; os?: string} = {};
    const s = ua || '';
    const pick = (re: RegExp, name: string) => {
        const m = re.exec(s);
        return m ? `${name} ${m[1]}` : '';
    };
    out.browser =
        pick(/Edg\/(\d+)/, 'Edge') ||
        pick(/OPR\/(\d+)/, 'Opera') ||
        pick(/Firefox\/(\d+)/, 'Firefox') ||
        pick(/Chrome\/(\d+)/, 'Chrome') ||
        pick(/Version\/(\d+).*Safari/, 'Safari');
    if (/Windows NT 10/.test(s)) {
        out.os = 'Windows 10/11';
    } else if (/Windows/.test(s)) {
        out.os = 'Windows';
    } else if (/Android (\d+)/.test(s)) {
        out.os = 'Android ' + /Android (\d+)/.exec(s)[1];
    } else if (/(iPhone|iPad).*OS (\d+)/.test(s)) {
        out.os = 'iOS ' + /OS (\d+)/.exec(s)[1];
    } else if (/Mac OS X/.test(s)) {
        out.os = 'macOS';
    } else if (/Linux/.test(s)) {
        out.os = 'Linux';
    }
    if (!out.browser) {
        delete out.browser;
    }
    if (!out.os) {
        delete out.os;
    }
    return out;
}

/**
 * The tenant's configured date format from the chat state (`GET api/assistant/chat` → `dateFormat`) for `env.dateFormat`;
 * null when the server sends none (no setting, older server) or something that is not a short format string.
 */
export function tenantDateFormat(value: any): string | null {
    if (typeof value !== 'string') {
        return null;
    }
    const format = value.trim();
    return format && format.length <= 40 ? format : null;
}

/** Map → plain object keeping the first `max` keys. */
export function firstKeys<V>(map: Map<string, V>, max: number): {[key: string]: V} {
    const out: {[key: string]: V} = {};
    let n = 0;
    map.forEach((v, k) => {
        if (n < max) {
            out[k] = v;
            n++;
        }
    });
    return out;
}

/**
 * Role-setting checks and tenant settings a screen read while rendering (instrumented getters write here; the context
 * service clears it on every navigation). Module-level on purpose: RoleService / SettingService feed it without a DI
 * dependency on the context service.
 */
export class ScreenReadLog {
    readonly checks = new Map<string, boolean>();
    readonly settings = new Map<string, string>();

    check(key: string, value: any): void {
        try {
            if (key && (this.checks.has(key) || this.checks.size < CHECKS_MAX)) {
                this.checks.set(clip(key, 80), value !== false);
            }
        } catch (e) {
            // never break a role check
        }
    }

    setting(key: string, value: any): void {
        try {
            if (key && (this.settings.has(key) || this.settings.size < SETTINGS_MAX)) {
                this.settings.set(clip(key, 80), settingValue(key, value));
            }
        } catch (e) {
            // never break a settings read
        }
    }

    clear(): void {
        this.checks.clear();
        this.settings.clear();
    }
}

export const screenReads = new ScreenReadLog();

/** "2 console errors, 1 failed request" for the pill tooltip ('' when there is nothing). */
export function diagnosticsSummary(ctx: AssistantScreenContext): string {
    if (!ctx) {
        return '';
    }
    const parts: string[] = [];
    const n = (count: number, one: string, many: string) => count && parts.push(`${count} ${count === 1 ? one : many}`);
    n((ctx.consoleErrors || []).length, 'console error', 'console errors');
    n((ctx.failedRequests || []).length, 'failed request', 'failed requests');
    n((ctx.visibleErrors || []).length, 'error on screen', 'errors on screen');
    n(((ctx.access && ctx.access.disabledActions) || []).length, 'disabled action', 'disabled actions');
    n(((ctx.screen && ctx.screen.form && ctx.screen.form.requiredEmpty) || []).length, 'required field empty', 'required fields empty');
    return parts.join(', ');
}

/**
 * Trims an attached context to `maxBytes` of JSON. Order (lead decision): trail → failedRequests → consoleErrors →
 * settings → the rest (access / env / visible errors / form extras), then the usual screen trimming (`capContext`).
 * Returns a new object.
 */
export function capAttachedContext(ctx: AssistantScreenContext, maxBytes = ATTACHED_CONTEXT_MAX_BYTES): AssistantScreenContext {
    const out: AssistantScreenContext = JSON.parse(JSON.stringify(ctx || {}));
    const fits = () => byteLength(out) <= maxBytes;
    const drain = (key: 'trail' | 'failedRequests' | 'consoleErrors') => {
        const list = out[key] as any[];
        while (!fits() && list && list.length) {
            list.shift();
        }
        if (list && !list.length) {
            delete out[key];
        }
    };
    drain('trail');
    drain('failedRequests');
    drain('consoleErrors');
    if (!fits()) {
        delete out.settings;
    }
    const form = out.screen && out.screen.form;
    const steps: Array<() => void> = [
        () => out.access && delete out.access.checks,
        () => out.env && delete out.env.slowRequests,
        () => form && delete form.apiFieldErrors,
        () => form && delete form.requiredEmpty,
        () => delete out.emptyState,
        () => out.access && delete out.access.hiddenActions,
        () => out.access && delete out.access.disabledActions,
        () => delete out.env,
        () => delete out.visibleErrors,
        () => delete out.access,
        () => form && delete form.step
    ];
    for (const step of steps) {
        if (fits()) {
            break;
        }
        step();
    }
    if (out.access && !Object.keys(out.access).length) {
        delete out.access;
    }
    return fits() ? out : capContext(out, maxBytes);
}
